import { useMemo } from "react";
import { countChanges, lineDiff, type DiffOp } from "../../lib/lineDiff";

export function PromptDiff({
  previous,
  current,
  previousLabel,
}: {
  previous: string | null;
  current: string;
  previousLabel: string;
}) {
  const previousText = previous ?? "";
  const ops = useMemo(
    () => lineDiff(previousText, current),
    [previousText, current],
  );
  const { added, removed } = countChanges(ops);
  const hasChanges = added > 0 || removed > 0;
  const summary = hasChanges
    ? `Changes vs ${previousLabel} (+${added} / −${removed})`
    : `No changes vs ${previousLabel}`;

  return (
    <details>
      <summary>{summary}</summary>
      {!previous && (
        <p className="card-desc" style={{ marginTop: 8 }}>
          No previous prompt — entire iteration shown as added.
        </p>
      )}
      <DiffBlock ops={ops} />
    </details>
  );
}

function DiffBlock({ ops }: { ops: DiffOp[] }) {
  return (
    <pre
      style={{
        whiteSpace: "pre-wrap",
        wordBreak: "break-word",
        fontFamily:
          "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
        fontSize: 12,
        lineHeight: 1.5,
        padding: 12,
        marginTop: 8,
        background: "var(--card)",
        color: "var(--text)",
        border: "1px solid var(--border)",
        borderRadius: 4,
        maxHeight: 480,
        overflow: "auto",
      }}
    >
      {ops.map((op, index) => (
        <span
          key={index}
          style={{
            display: "block",
            background:
              op.type === "added"
                ? "rgba(74, 222, 128, 0.12)"
                : op.type === "removed"
                ? "rgba(255, 107, 107, 0.12)"
                : "transparent",
            color:
              op.type === "added"
                ? "var(--success)"
                : op.type === "removed"
                ? "var(--error)"
                : "var(--muted)",
          }}
        >
          {op.type === "added" ? "+ " : op.type === "removed" ? "− " : "  "}
          {op.line || " "}
        </span>
      ))}
    </pre>
  );
}

export function PromptBlock({ text }: { text: string }) {
  return (
    <pre
      style={{
        whiteSpace: "pre-wrap",
        wordBreak: "break-word",
        fontFamily:
          "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
        fontSize: 12,
        lineHeight: 1.5,
        padding: 12,
        marginTop: 8,
        background: "var(--card)",
        color: "var(--text)",
        border: "1px solid var(--border)",
        borderRadius: 4,
        maxHeight: 480,
        overflow: "auto",
      }}
    >
      {text}
    </pre>
  );
}
